
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Question } from "@/types/test";
import { 
  showSuccessToast, 
  showErrorToast 
} from "@/utils/toast-utils";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useNavigate } from "react-router-dom";
import { TestDetails } from "./useTestDetails";

export const useTestSave = (testId: string | null) => {
  const { user } = useAuth();
  const { subscription, refreshSubscription } = useSubscription();
  const navigate = useNavigate();

  const saveTest = async (
    testDetails: TestDetails,
    questions: Question[],
    publish: boolean = false
  ) => {
    try {
      if (!user && publish) {
        throw new Error("You must be signed in to publish a test");
      }
      
      // Check subscription limits for new test publishing
      if (publish && !testId) {
        if (!subscription) {
          throw new Error("Unable to verify subscription status. Please try again.");
        }
        
        // No test count limits anymore - only evaluation limits apply via triggers
      }

      const testData = {
        title: testDetails.title,
        description: testDetails.description,
        creator_id: user?.id,
        key_responsibilities: "", // We're merging this into description
        years_of_experience: testDetails.yearsOfExperience,
        location: testDetails.location,
        key_skills: testDetails.keySkills,
        time_limit: testDetails.timeLimit,
        status: publish ? "published" : "draft",
        publish_to_job_board: testDetails.publishToJobBoard,
        work_arrangement: testDetails.workArrangement,
        salary_range_min: testDetails.salaryRangeMin,
        salary_range_max: testDetails.salaryRangeMax,
        salary_currency: testDetails.salaryCurrency || 'INR',
        language: "English",
        share_token: crypto.randomUUID(),
        webcam_monitoring_enabled: testDetails.webcamMonitoringEnabled ?? true,
        invitation_only: testDetails.invitationOnly ?? false,
        is_scheduled: testDetails.isScheduled ?? false,
        scheduled_start_time: testDetails.scheduledStartTime ? testDetails.scheduledStartTime.toISOString() : null,
        scheduled_end_time: testDetails.scheduledEndTime ? testDetails.scheduledEndTime.toISOString() : null,
        schedule_timezone: testDetails.scheduleTimezone || 'UTC',
        welcome_message: testDetails.welcomeMessage || null,
        completion_message: testDetails.completionMessage || null,
        // CRITICAL: Only set is_active on INSERT (new test creation), not on UPDATE
        // This prevents infinite loops and allows Edge Function and users to control activation
        ...(testId ? {} : { is_active: testDetails.isScheduled ? false : true }),
      };

      let testResponse;
      if (testId) {
        // Update existing test (don't update share_token if it already exists)
        const updateData = { ...testData };
        delete updateData.share_token; // Remove share_token to prevent overwriting existing one
        
        const { data, error } = await supabase
          .from("tests")
          .update(updateData)
          .eq("id", testId)
          .select()
          .single();
            
        if (error) throw error;
        testResponse = data;
      } else {
        // Create new test with share_token
        const { data, error } = await supabase
          .from("tests")
          .insert(testData)
          .select()
          .single();
          
        if (error) throw error;
        testResponse = data;
      }

      // Save questions with order field, skill, difficulty and markscheme
      const questionsData = questions.map((q, index) => ({
        test_id: testResponse.id,
        question_type: q.question_type,
        content: q.content,
        time_limit: q.time_limit,
        marks: q.marks || 1,
        order: index,
        skill: q.skill,
        difficulty: q.difficulty,
        markscheme: q.markscheme, // Include markscheme data for storage
        code_tests: q.code_tests // Include code tests for coding questions
      }));

      if (testId) {
        const { error: deleteError } = await supabase
          .from("questions")
          .delete()
          .eq("test_id", testId);

        if (deleteError) throw deleteError;
      }

      const { error: questionsError } = await supabase
        .from("questions")
        .insert(questionsData);

      if (questionsError) throw questionsError;

      // REMOVED: Edge function call to update-subscription for test creation
      // The database trigger will handle this automatically

      // Refresh subscription data to get the latest usage
      if (publish && !testId) {
        await refreshSubscription();
      }

      // Show appropriate toast message
      if (publish) {
        showSuccessToast({
          title: "Test Published!",
          description: "Your test is now available for candidates"
        });
      } else {
        showSuccessToast({
          title: "Test Saved",
          description: "Your test has been saved as a draft"
        });
      }

      return testResponse;
    } catch (error: any) {
      console.error('Error in saveTest:', error);
      showErrorToast({
        title: "Error",
        description: error.message
      });
      throw error;
    }
  };

  return { saveTest };
};
